/**
 * PhpVersionPanel.tsx
 *
 * Customer-facing PHP version selector for a hosting account.
 * - Reads current version + available options from GET /api/hosting/accounts/:id/php-version
 * - Switches via PUT /api/hosting/accounts/:id/php-version
 * - Reload takes ~3-5 seconds (rewrites pool, reloads old + new FPM, reloads nginx).
 *   We block the UI during the switch and refetch after.
 */
import React, { useEffect, useState } from 'react';

interface Props { hostingAccountId: string; }

const apiBase = process.env.REACT_APP_API_URL || '';
function authFetch(path: string, init: RequestInit = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${apiBase}${path}`, {
    ...init,
    headers: {
      ...(init.headers || {}),
      'Authorization': `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    },
  });
}

const PhpVersionPanel: React.FC<Props> = ({ hostingAccountId }) => {
  const [current, setCurrent] = useState<string | null>(null);
  const [options, setOptions] = useState<string[]>([]);
  const [target, setTarget] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await authFetch(`/api/hosting/accounts/${hostingAccountId}/php-version`);
      const j = await r.json();
      if (j.success) {
        setCurrent(j.current);
        setOptions(j.options || []);
        setTarget(j.current);
      } else {
        setErr(j.error || 'Failed to load PHP version');
      }
    } catch (e: any) { setErr(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hostingAccountId]);

  const submit = async () => {
    if (!target || target === current) return;
    if (!window.confirm(`Switch PHP from ${current} to ${target}? Your site will briefly reload but stays online (~3-5 seconds). Plugin/theme code that uses features removed in newer PHP versions may break.`)) {
      return;
    }
    setBusy(true);
    setErr(''); setOk('');
    try {
      const r = await authFetch(`/api/hosting/accounts/${hostingAccountId}/php-version`, {
        method: 'PUT',
        body: JSON.stringify({ version: target }),
      });
      const j = await r.json();
      if (j.success) {
        setOk(`PHP version is now ${j.newVersion}.`);
        await load();
      } else {
        setErr(j.error || 'Switch failed');
      }
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <h2 className="font-semibold text-gray-900 mb-1">PHP Version</h2>
      <p className="text-xs text-gray-500 mb-4">
        Choose the PHP runtime your site uses. Switching is instant — only a brief FPM reload (~3-5s)
        with no downtime visible to users. Older versions (7.4) are kept available for legacy apps,
        but we recommend 8.1 or newer for new sites (better performance + security).
      </p>

      {err && <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{err}</div>}
      {ok && <div className="mb-3 bg-green-50 border border-green-200 rounded p-2 text-sm text-green-800">{ok}</div>}

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (
        <div className="space-y-4">
          <div className="text-sm">
            Currently running: <strong className="text-gray-900">PHP {current}</strong>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-2">Switch to</label>
            <div className="flex flex-wrap gap-2">
              {options.map(v => (
                <button
                  key={v}
                  onClick={() => setTarget(v)}
                  disabled={busy}
                  className={`px-3 py-1.5 rounded text-sm border transition ${
                    target === v
                      ? 'bg-teal-600 text-white border-teal-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  } ${v === current ? 'ring-1 ring-teal-400' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  PHP {v}
                  {v === current && <span className="ml-1 text-xs opacity-80">(current)</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={submit}
              disabled={busy || !target || target === current}
              className="text-sm bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? 'Switching…' : target && target !== current ? `Apply PHP ${target}` : 'No change'}
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500 space-y-1">
        <div><strong>PHP 8.3</strong> — latest, fastest, most secure. Recommended for new sites.</div>
        <div><strong>PHP 8.2 / 8.1</strong> — well-supported, broadly compatible.</div>
        <div><strong>PHP 8.0</strong> — older, kept for compatibility.</div>
        <div><strong>PHP 7.4</strong> — legacy. Only use if your app explicitly requires it (no longer security-patched upstream).</div>
      </div>
    </div>
  );
};

export default PhpVersionPanel;
